import type { PortfolioTaskTemplateResource } from "api/generated";
import type {
  LibraryTask,
  LibraryTaskAttachment,
  LibraryTaskLink,
  LibraryTaskMeta,
  LibraryTaskSkill,
  LibraryTaskTag,
  LibraryTaskUser,
  LibraryTaskVideo,
} from "portfolio/types";
import { taskQuestionFromDto } from "./taskQuestion.mapper";
import { mapAttachmentMediaFlags } from "shared/mappers/attachment.mapper";

const mapUser = (
  user: PortfolioTaskTemplateResource["owners"][number],
): LibraryTaskUser => ({
  id: user.id,
  fullName: user.full_name,
  shortage: user.shortage ?? null,
  profileImage: user.profile_image ?? null,
});

const mapTag = (
  tag: PortfolioTaskTemplateResource["tags"][number],
): LibraryTaskTag => ({
  id: tag.id,
  parentId: tag.parent_id,
  name: tag.name,
  educationLevels: tag.education_levels,
});

const mapSkill = (
  skill: PortfolioTaskTemplateResource["skills"][number],
): LibraryTaskSkill => ({
  id: skill.id,
  name: skill.name,
  description: skill.description,
  categoryId: skill.category_id ?? null,
  colorCode: skill.color_code ?? null,
  educationLevels: skill.education_levels,
});

const mapAttachment = (
  attachment: PortfolioTaskTemplateResource["attachments"][number],
): LibraryTaskAttachment => ({
  id: attachment.id,
  name: attachment.name,
  attachment: attachment.attachment,
  url: attachment.url,
  previewUrl: attachment.preview_url ?? null,
  mimetype: attachment.mimetype,
  transcode_status: attachment.transcode_status,
  stream_url: attachment.stream_url,
  thumbnail_url: attachment.thumbnail_url,
  ...mapAttachmentMediaFlags(attachment),
});

const mapLink = (
  link: PortfolioTaskTemplateResource["links"][number],
): LibraryTaskLink => ({
  id: link.id,
  name: link.name,
  url: link.url,
});

const mapVideo = (
  video: PortfolioTaskTemplateResource["videos"][number],
): LibraryTaskVideo => ({
  id: video.id,
  name: video.name,
  url: video.url,
});

const mapLibraryTaskMeta = (
  meta: PortfolioTaskTemplateResource["meta"],
): LibraryTaskMeta => ({
  canUpdate: meta.can_update,
  canDelete: meta.can_delete,
});

export const libraryTaskFromDto = (
  resource: PortfolioTaskTemplateResource,
): LibraryTask => ({
  id: resource.id,
  publicPortfolioTaskId: resource.public_portfolio_task_id,
  user: resource.user ? mapUser(resource.user) : null,
  type: resource.type,
  templateName: resource.template_name,
  title: resource.title,
  description: resource.description,
  teacherInstructions: resource.teacher_instructions ?? null,
  bannerImage: resource.banner_image,
  tagIds: resource.tag_ids,
  tags: resource.tags.map(mapTag),
  skillIds: resource.skill_ids,
  skills: resource.skills.map(mapSkill),
  lobCompetenceIds: resource.lob_competence_ids,
  lobCompetences: resource.lob_competences.map((competence) => ({
    id: competence.id,
    name: competence.name,
  })),
  attachmentRequired: resource.attachment_required,
  allowTaskRatings: resource.allow_task_ratings,
  assessmentModels: Array.isArray(resource.assessment_models)
    ? (resource.assessment_models[0] ?? null)
    : (resource.assessment_models ?? null),
  attachments: resource.attachments.map(mapAttachment),
  links: resource.links.map(mapLink),
  videos: resource.videos.map(mapVideo),
  owners: resource.owners.map(mapUser),
  subjects: resource.subjects,
  educationLevels: resource.education_levels,
  questions: resource.questions.map(taskQuestionFromDto),
  createdAt: resource.created_at ? new Date(resource.created_at) : new Date(),
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : new Date(),
  meta: mapLibraryTaskMeta(resource.meta),
});
